import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Card, CardContent, Box, Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px !important",
    margin: "0px !important",
    width: "100%",
    padding: "0px !important",
  },
  card: {
    marginBottom: theme.spacing(2),
    transition: "all 0.3s ease-in-out",
    cursor: "pointer",
    boxShadow: `4px 4px 8px rgba(189, 194, 199, 0.75),
                  8px 8px 12px rgba(189, 194, 199, 0.25),
                 -4px -4px 8px rgba(255, 255, 255, 0.75),
                 -8px -8px 12px rgba(255, 255, 255, 0.25)`,
    background: "#EBEFF3",
    borderRadius: "16px",
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
    "&:last-child": {
      paddingBottom: theme.spacing(1.5),
    },
    backgroundColor: " #EBEFF3",
  },
}));

const colorPalette = [
  [" #57D3DB", " #3FC5CE", " #2BB5C0"],
  [" #A83B96", " #EB77D8", " #A83B96"], // Pinkish purple
  [" #3358B8", " #5585FF", " #3358B8"], // Royal blue
  [" #FF5733", " #FF8F6B", " #FF5733"], // Orange red
  [" #C70039", " #FF6F7C", " #C70039"], // Crimson red
  [" #900C3F", " #D26A90", " #900C3F"], // Dark wine
  [" #581845", " #8F5E75", " #581845"], // Deep purple
  [" #1F77B4", " #5FA7E3", " #1F77B4"], // Blue
  [" #FF7F0E", " #FFB366", " #FF7F0E"], // Soft orange
  [" #2CA02C", " #6FD46F", " #2CA02C"], // Green
  [" #D62728", " #FF7F7F", " #D62728"], // Bright red
  [" #9467BD", " #C49BDF", " #9467BD"], // Lavender purple
  [" #8C564B", " #B98D83", " #8C564B"], // Earth brown
  [" #E377C2", " #F4A9E0", " #E377C2"], // Cotton pink
  [" #7F7F7F", " #B5B5B5", " #7F7F7F"], // Gray
  [" #BCBD22", " #E0E142", " #BCBD22"], // Acid green
  [" #17BECF", " #64E8F7", " #17BECF"], // Aqua cyan
  [" #FFBB28", " #FFDD80", " #FFBB28"], // Amber yellow
];

const getColorForSubject = (index) => {
  return colorPalette[index % colorPalette.length];
};

const CustomLegend = ({ payload, checkedCount }) => {
  if (!payload) return null;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexWrap="wrap"
      alignItems="center"
    >
      {/* Subject Legends */}
      <Box display="flex" flexWrap="wrap" alignItems="center">
        {payload.map((entry, index) => {
          const colors = getColorForSubject(index);
          return (
            <Box
              key={entry.value}
              display="flex"
              alignItems="center"
              mr={2}
              mb={0.5}
            >
              <Box
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: colors[0],
                  marginRight: "6px",
                }}
              />
              <Typography fontSize="14px" style={{ fontWeight: "bold" }}>
                {entry.value.slice(0, 3).toUpperCase()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Checked Count */}
      <Typography fontSize="14px" style={{ fontWeight: "bold" }}>
        {checkedCount}
      </Typography>
    </Box>
  );
};

const StackedBarChart = ({ subjects, data, checkedCount }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const subjectKeys = Object.keys(subjects);

  const gradientDefinitions = subjectKeys.map((subject, index) => {
    const gradientId = `color${subject}`;
    const colors = getColorForSubject(index);

    return (
      <linearGradient
        id={gradientId}
        key={gradientId}
        x1="0"
        y1="0"
        x2="1"
        y2="0"
      >
        <stop offset="0%" stopColor={colors[0]} />
        <stop offset="52%" stopColor={colors[1]} />
        <stop offset="100%" stopColor={colors[2]} />
      </linearGradient>
    );
  });

  const transformedData =
    data &&
    data[0] !== null &&
    data
      .filter(
        (item) =>
          item.result && item.result !== "NA" && typeof item.result === "object"
      )
      .map((item, index) => {
        let label = "";
        const isExamTypeAverage = item.isExamTypeAverage || false;

        if (isExamTypeAverage) {
          label = item.result.examtype
            ? item.result.examtype.slice(0, 3).toUpperCase()
            : "";
        } else if (item.testDisplayName.includes("Average")) {
          label = "Avg";
        } else {
          //label = `T${index + 1}`;
          label = item.test_graph_name;
        }

        return {
          name: label,
          fullName: item.testDisplayName,
          isExamTypeAverage,
          ...item.result,
        };
      });

  const reversedData = transformedData ? [...transformedData].reverse() : [];

  // Limit the number of bars based on screen size
  const limitedData = reversedData.slice(
    0,
    isMobile ? 7 : isTablet ? 8 : reversedData.length
  );

  // Create shadow data where each shadow bar uses the item's total value
  const shadowData = limitedData.map((item) => {
    const shadowItem = { ...item };
    const itemTotal = item.total || 100;

    // For shadow bars, use the total value for each subject to create full height
    subjectKeys.forEach((subject, index) => {
      // Distribute the total across subjects proportionally for shadow effect
      // or use the total for the first subject and 0 for others to create a single shadow bar
      if (index === 0) {
        shadowItem[`${subject}_shadow`] = itemTotal;
      } else {
        shadowItem[`${subject}_shadow`] = 0;
      }
    });
    return shadowItem;
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataIndex = transformedData.findIndex(
        (item) => item.name === label
      );
      const dataItem = transformedData[dataIndex];

      return (
        <div
          className="custom-tooltip"
          style={{
            padding: "10px",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            minWidth: "180px",
          }}
        >
          <p
            className="label"
            style={{ fontWeight: "bold", marginBottom: "10px" }}
          >
            {dataItem.fullName}
          </p>
          <p style={{ margin: "5px 0", fontWeight: 500 }}>
            Total Marks:{" "}
            <span style={{ color: "#3358B8" }}>
              {dataItem.Marks || 0} / {dataItem.total || 0}
            </span>
          </p>
          <p style={{ margin: "5px 0", fontWeight: 500 }}>
            Total %:{" "}
            <span style={{ color: "#46AB87" }}>
              {dataItem.percentage || 0}%
            </span>
          </p>
          <hr style={{ margin: "8px 0" }} />
          {subjectKeys.map((subject, index) => {
            const colors = getColorForSubject(index);
            const percentKey = `${subject}Percent`;
            const totalKey = `${subject}Total`;
            return (
              <div key={subject} style={{ marginBottom: "4px" }}>
                <span style={{ color: colors[0], fontWeight: 500 }}>
                  {subject}:
                </span>{" "}
                <span>
                  {dataItem[subject] || 0}
                  {label === "Avg"
                    ? ` (${dataItem[percentKey] || 0}%)`
                    : ` / ${dataItem[totalKey] || 0} (${dataItem[percentKey] ||
                        0}%)`}
                </span>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  /*--- FINDING THE TEST TYPE AND FIXED THE RANGE FOR (JEE MAINS , NEET) ---*/
  const hasJeeMains = data.some(
    (item) => item.test_type && item.test_type.toLowerCase() === "jee mains"
  );

  const hasNeet = data.some(
    (item) => item.test_type && item.test_type.toLowerCase() === "neet"
  );

  const hasCustom = data.some(
    (item) => item.test_type && item.test_type.toLowerCase() === "custom"
  );

  const hasJeeAdvanced = data.some(
    (item) => item.test_type && item.test_type.toLowerCase() === "jee advanced"
  );

  const getHighestTotalByTestType = (data, testType) => {
    return data
      .filter(
        (item) =>
          item.test_type.toLowerCase() === testType.toLowerCase() &&
          item.result &&
          item.result !== "NA" &&
          typeof item.result.total === "number"
      )
      .reduce(
        (maxObj, item) => {
          if (item.result.total > maxObj.highestTotal) {
            return {
              test_type: testType,
              highestTotal: item.result.total,
            };
          }
          return maxObj;
        },
        { test_type: testType, highestTotal: 0 }
      );
  };

  const customHighest = getHighestTotalByTestType(data, "CUSTOM");
  const jeeAdvancedHighest = getHighestTotalByTestType(data, "JEE ADVANCED");

  const generateSmartTicks = (max, steps = 5) => {
    if (!max || typeof max !== "number") return undefined;
    const interval = Math.ceil(max / steps);
    const ticks = [];
    for (let i = 0; i < steps; i++) {
      ticks.push(i * interval);
    }
    if (ticks[ticks.length - 1] !== max) {
      ticks.push(max);
    }
    return ticks;
  };

  return (
    subjects &&
    Object.keys(subjects).length !== 0 && (
      <div>
        <CustomLegend
          payload={subjectKeys.map((subject, index) => ({
            value: subject,
            color: getColorForSubject(index)[0],
          }))}
          checkedCount={checkedCount}
        />
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={shadowData}
                style={{ marginLeft: "-40px", marginRight: "-40px" }}
                barCategoryGap={0}
                barGap={-23}
              >
                <svg width="0" height="0">
                  <defs>
                    {gradientDefinitions}

                    {/* Deep inset shadow for the background bar */}
                    <filter
                      id="deepInsetShadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="300%"
                    >
                      <feOffset dx="0" dy="2" />
                      <feGaussianBlur stdDeviation="2" result="offsetBlur" />
                      <feComposite
                        in="SourceAlpha"
                        in2="offsetBlur"
                        operator="out"
                        result="innerShadow"
                      />
                      <feFlood
                        floodColor=" #c0c0c0"
                        floodOpacity="0.9"
                        result="color"
                      />
                      <feComposite
                        in="color"
                        in2="innerShadow"
                        operator="in"
                        result="shadow"
                      />
                      <feComposite
                        in="shadow"
                        in2="SourceGraphic"
                        operator="over"
                      />
                    </filter>

                    {/* Drop shadow for the colored progress arc */}
                    <filter
                      id="dropShadow"
                      x="-10%"
                      y="-10%"
                      width="120%"
                      height="120%"
                    >
                      <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="1"
                        floodColor=" #bbb"
                        floodOpacity="0.5"
                      />
                    </filter>
                  </defs>
                </svg>
                {/* <CartesianGrid strokeDasharray="3 3" vertical={false} /> */}
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  fontWeight="bold"
                />
                <YAxis
                  {...{
                    domain: hasJeeMains
                      ? [0, () => 300]
                      : hasNeet
                      ? [0, () => 720]
                      : hasCustom
                      ? [0, () => customHighest && customHighest.highestTotal]
                      : hasJeeAdvanced
                      ? [
                          0,
                          () =>
                            jeeAdvancedHighest &&
                            jeeAdvancedHighest.highestTotal,
                        ]
                      : [0, "auto"],
                    ticks: hasNeet
                      ? [0, 150, 300, 450, 600, 720]
                      : hasCustom
                      ? generateSmartTicks(customHighest.highestTotal)
                      : hasJeeAdvanced
                      ? generateSmartTicks(jeeAdvancedHighest.highestTotal)
                      : undefined,
                  }}
                  axisLine={false}
                  tickLine={false}
                  fontWeight="bold"
                />
                <Tooltip content={<CustomTooltip />} />
                {/* Shadow bars - render first to appear behind main bars */}
                {subjectKeys.map((subject, index) => (
                  <Bar
                    key={`shadow-${subject}`}
                    dataKey={`${subject}_shadow`}
                    stackId="shadow"
                    barSize={24}
                    radius={[12, 12, 12, 12]}
                  >
                    {shadowData.map((_, entryIndex) => (
                      <Cell
                        key={`shadow-cell-${subject}-${entryIndex}`}
                        fill="#E0E4E8"
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        filter="url(#deepInsetShadow)"
                      />
                    ))}
                  </Bar>
                ))}
                {/* Main bars */}
                {subjectKeys.map((subject, index) => {
                  const isFirst = index === 0;
                  const isLast = index === subjectKeys.length - 1;
                  return (
                    <Bar
                      key={subject}
                      dataKey={subject}
                      stackId="a"
                      barSize={22}
                      radius={
                        isLast
                          ? [12, 12, 0, 0]
                          : isFirst
                          ? [0, 0, 12, 12]
                          : [0, 0, 0, 0]
                      }
                      fill={`url(#color${subject})`}
                    >
                      {limitedData.map((_, entryIndex) => (
                        <Cell key={`cell-${subject}-${entryIndex}`} />
                      ))}
                    </Bar>
                  );
                })}
                <Bar dataKey="percentage" fill="transparent" stackId="shadow">
                  <LabelList
                    dataKey="percentage"
                    content={({ x, y, width, height, value }) => {
                      const centerX = x + width / 2;
                      const labelY = y + height * 0.5;

                      return (
                        <text
                          x={centerX}
                          y={labelY}
                          textAnchor="middle"
                          transform={`rotate(-90, ${centerX}, ${labelY})`}
                          fontSize={10}
                          fontWeight="bold"
                          fill="#000"
                        >
                          {`${Math.round(value)}%`}
                        </text>
                      );
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    )
  );
};

export default StackedBarChart;
